import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Modal,
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Empty,
  Tooltip,
  Switch,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Utility functions
const parseValue = (value) => {
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return {};
  }
};

const formatValueForDisplay = (val) => {
  return typeof val === 'object' ? JSON.stringify(val) : val.toString();
};

const isObjectValue = (val) => typeof val === 'object' && val !== null;

const tryParseJSON = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
};

// Sub-components
const ValuePreview = ({ value }) => {
  if (!value) return <Text type="secondary">Empty</Text>;

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const truncateItem = (item) => {
        const itemStr = String(item);
        return itemStr.length > 30 ? `${itemStr.substring(0, 30)}...` : itemStr;
      };

      const arrayPreview =
        parsed.length > 0
          ? `[${parsed
              .slice(0, 2)
              .map((item) => `"${truncateItem(item)}"`)
              .join(', ')}${
              parsed.length > 2 ? `, +${parsed.length - 2} more` : ''
            }]`
          : '[]';
      return <Text style={{ color: '#52c41a' }}>{arrayPreview}</Text>;
    }
  } catch {
    // Not JSON, treat as string
  }

  const preview = value.length > 50 ? `${value.substring(0, 50)}...` : value;
  return <Text>{preview}</Text>;
};

const KeyValueCard = ({ pair, index, onEdit, onDelete, canDelete }) => (
  <Card
    size="small"
    style={{ marginBottom: 8, cursor: 'pointer' }}
    bodyStyle={{ padding: '12px 16px' }}
    hoverable
    onClick={() => onEdit(index)}
  >
    <Row align="middle" justify="space-between">
      <Col flex="auto">
        <div>
          <Text strong>{pair.key || `Section ${index + 1}`}</Text>
          <div style={{ marginTop: 4 }}>
            <ValuePreview value={pair.value} />
          </div>
        </div>
      </Col>
      <Col>
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(index);
              }}
            />
          </Tooltip>
          {canDelete && (
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                type="text"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
              />
            </Tooltip>
          )}
        </Space>
      </Col>
    </Row>
  </Card>
);

const ArrayEditor = ({ items, onItemsChange }) => {
  const addItem = () => onItemsChange([...items, '']);

  const removeItem = (index) => {
    if (items.length > 1) {
      onItemsChange(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    onItemsChange(updated);
  };

  const getItemHeight = () => {
    const count = items.length;
    if (count === 1) return '484px';
    if (count === 2) return '238px';
    if (count === 3) return '156px';
    if (count === 4) return '115px';
    return '80px';
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Text strong>Prompt Items:</Text>
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={addItem}
        >
          Add Prompt
        </Button>
      </div>
      <div
        style={{
          height: '500px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              gap: 8,
              height: items.length <= 4 ? getItemHeight() : 'auto',
              minHeight: items.length > 4 ? '80px' : 'auto',
            }}
          >
            <Input.TextArea
              placeholder={`Prompt ${index + 1}`}
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              size="large"
              style={{ flex: 1, minWidth: 0, resize: 'none', height: '100%' }}
              autoSize={false}
            />
            {items.length > 1 && (
              <Button
                icon={<DeleteOutlined />}
                onClick={() => removeItem(index)}
                danger
                size="large"
                style={{ alignSelf: 'flex-start', flexShrink: 0 }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EditModal = ({
  visible,
  editingPair,
  isArrayMode,
  arrayItems,
  onSave,
  onCancel,
  onPairChange,
  onArrayModeToggle,
  onArrayItemsChange,
}) => (
  <Modal
    title={editingPair.isEditing ? 'Manage Section' : 'Add New Section'}
    open={visible}
    onOk={onSave}
    onCancel={onCancel}
    width={900}
    okText="Save"
    cancelText="Cancel"
    style={{ top: 20 }}
    bodyStyle={{ minHeight: '700px', maxHeight: '90vh', overflowY: 'auto' }}
  >
    <div style={{ padding: '16px 0', width: '100%', boxSizing: 'border-box' }}>
      <Row gutter={16} style={{ width: '100%', margin: 0 }}>
        <Col span={24} style={{ marginBottom: 16 }}>
          <Text strong>Section Name:</Text>
          <Input
            placeholder="Enter section name"
            value={editingPair.key}
            onChange={(e) => onPairChange('key', e.target.value)}
            size="large"
            style={{ marginTop: 4 }}
          />
        </Col>
        <Col span={24} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>Prompt Type:</Text>
            <Switch
              checked={isArrayMode}
              onChange={onArrayModeToggle}
              checkedChildren="Multiple"
              unCheckedChildren="Single"
            />
            <Text type="secondary">
              {isArrayMode ? 'Multiple prompts' : 'Single prompt'}
            </Text>
          </div>
        </Col>
        <Col span={24}>
          {isArrayMode ? (
            <ArrayEditor
              items={arrayItems}
              onItemsChange={onArrayItemsChange}
            />
          ) : (
            <div>
              <Text strong>Prompt:</Text>
              <Input.TextArea
                rows={20}
                placeholder="Enter prompt text"
                value={editingPair.value}
                onChange={(e) => onPairChange('value', e.target.value)}
                size="large"
                style={{ marginTop: 4, width: '100%', resize: 'vertical' }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  </Modal>
);

const KeyValuePairEditor = ({ value, onChange }) => {
  const [keyValuePairs, setKeyValuePairs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingPair, setEditingPair] = useState({
    key: '',
    value: '',
    isObject: false,
    isEditing: false,
  });
  const [isArrayMode, setIsArrayMode] = useState(false);
  const [arrayItems, setArrayItems] = useState(['']);

  // Memoized data processing
  const processedData = useMemo(() => {
    const data = parseValue(value);

    if (!data) {
      return [{ key: '', value: '', isObject: false }];
    }

    if (typeof data === 'object' && !Array.isArray(data)) {
      return Object.entries(data).map(([key, val]) => ({
        key,
        value: formatValueForDisplay(val),
        isObject: isObjectValue(val),
      }));
    }

    if (Array.isArray(data)) {
      return data.map((item, index) => ({
        key: index.toString(),
        value: formatValueForDisplay(item),
        isObject: isObjectValue(item),
      }));
    }

    return [{ key: '', value: data.toString(), isObject: false }];
  }, [value]);

  useEffect(() => {
    setKeyValuePairs(processedData);
  }, [processedData]);

  // Memoized data update function
  const updateParentData = useCallback(
    (pairs) => {
      const isArray = pairs.every((pair) => !isNaN(Number(pair.key)));
      let result;

      if (isArray) {
        result = [];
        pairs.forEach((pair) => {
          const index = Number(pair.key);
          result[index] = tryParseJSON(pair.value);
        });
        result = result.filter((item) => item !== undefined);
      } else {
        result = {};
        pairs.forEach((pair) => {
          if (pair.key.trim()) {
            result[pair.key] = tryParseJSON(pair.value);
          }
        });
      }

      onChange(JSON.stringify(result));
    },
    [onChange]
  );

  const _handleKeyValueChange = useCallback(
    (index, field, newValue) => {
      const updatedPairs = [...keyValuePairs];
      updatedPairs[index][field] = newValue;
      setKeyValuePairs(updatedPairs);
      updateParentData(updatedPairs);
    },
    [keyValuePairs, updateParentData]
  );

  const openEditModal = useCallback(
    (index = null) => {
      if (index !== null) {
        const pair = keyValuePairs[index];
        setEditingIndex(index);
        setEditingPair({ ...pair, isEditing: true });

        // Check if value is an array
        try {
          const parsedValue = JSON.parse(pair.value);
          const isStringArray =
            Array.isArray(parsedValue) &&
            parsedValue.every((item) => typeof item === 'string');

          setIsArrayMode(isStringArray);
          setArrayItems(
            isStringArray && parsedValue.length > 0 ? parsedValue : ['']
          );
        } catch {
          setIsArrayMode(false);
          setArrayItems(['']);
        }
      } else {
        setEditingIndex(null);
        setEditingPair({
          key: '',
          value: '',
          isObject: false,
          isEditing: false,
        });
        setIsArrayMode(false);
        setArrayItems(['']);
      }
      setIsModalVisible(true);
    },
    [keyValuePairs]
  );

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
    setEditingIndex(null);
    setEditingPair({ key: '', value: '', isObject: false, isEditing: false });
    setIsArrayMode(false);
    setArrayItems(['']);
  }, []);

  const toggleArrayMode = useCallback(
    (checked) => {
      setIsArrayMode(checked);
      if (checked) {
        if (arrayItems.length > 1) {
          const updatedItems = [...arrayItems];
          updatedItems[0] = editingPair.value.trim() || arrayItems[0];
          setArrayItems(updatedItems);
        } else {
          const currentValue = editingPair.value.trim();
          setArrayItems(currentValue ? [currentValue] : ['']);
        }
      } else {
        setEditingPair((prev) => ({
          ...prev,
          value: arrayItems.length > 0 ? arrayItems[0] : '',
        }));
      }
    },
    [editingPair.value, arrayItems]
  );

  const handlePairChange = useCallback((field, value) => {
    setEditingPair((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveEditingPair = useCallback(() => {
    let pairToSave = { ...editingPair };

    if (isArrayMode) {
      const filteredItems = arrayItems.filter((item) => item.trim() !== '');
      pairToSave.value = JSON.stringify(filteredItems);
      pairToSave.isObject = true;
    }

    const updatedPairs =
      editingIndex !== null
        ? keyValuePairs.map((pair, idx) =>
            idx === editingIndex ? pairToSave : pair
          )
        : [...keyValuePairs, pairToSave];

    setKeyValuePairs(updatedPairs);
    updateParentData(updatedPairs);
    closeModal();
  }, [
    editingPair,
    isArrayMode,
    arrayItems,
    editingIndex,
    keyValuePairs,
    updateParentData,
    closeModal,
  ]);

  const removeKeyValuePair = useCallback(
    (index) => {
      if (keyValuePairs.length > 1) {
        const updatedPairs = keyValuePairs.filter((_, i) => i !== index);
        setKeyValuePairs(updatedPairs);
        updateParentData(updatedPairs);
      }
    },
    [keyValuePairs, updateParentData]
  );

  if (keyValuePairs.length === 0) {
    return <Empty description="No data" />;
  }

  return (
    <div className="key-value-pair-editor">
      {/* Header with Add button */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openEditModal()}
          size="middle"
        >
          Add Section
        </Button>
      </div>

      {/* List of key-value pairs */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {keyValuePairs.map((pair, index) => (
          <KeyValueCard
            key={index}
            pair={pair}
            index={index}
            onEdit={openEditModal}
            onDelete={removeKeyValuePair}
            canDelete={keyValuePairs.length > 1}
          />
        ))}
      </div>

      {/* Edit Modal */}
      <EditModal
        visible={isModalVisible}
        editingPair={editingPair}
        isArrayMode={isArrayMode}
        arrayItems={arrayItems}
        onSave={saveEditingPair}
        onCancel={closeModal}
        onPairChange={handlePairChange}
        onArrayModeToggle={toggleArrayMode}
        onArrayItemsChange={setArrayItems}
      />
    </div>
  );
};

export default KeyValuePairEditor;
